import {
  Client,
  Database_Procs_RankingsIndividualSubscription_Item,
  Database_Procs_RankingsPlayerInternational_Item,
  Database_Procs_RankingsPlayerLocal_Item,
  Database_Procs_RankingsPlayerNation_Item,
  Database_Procs_RankingsPlayerState_Item,
  Database_Procs_RankingsSchoolInternational_Item,
  Database_Procs_RankingsSchoolLocal_Item,
  Database_Procs_RankingsSchoolNation_Item,
  Database_Procs_RankingsSchoolState_Item,
  Database_Procs_RankingsTeamInternational_Item,
  Database_Procs_RankingsTeamLocal_Item,
  Database_Procs_RankingsTeamNation_Item,
  Database_Procs_RankingsTeamState_Item,
} from '@suntex.fim/api-client';
import { RankingLevel, SpecialGradeLevel } from '@suntex.fim/enums';
import {
  ModularColumnDef,
  ModularTableDef,
  defineNumericColumn,
  defineTable,
  defineTextColumn,
} from '@suntex.fim/ui';

import { RankingsSlug } from './const';

export type RankingResponseDataType =
  | Database_Procs_RankingsIndividualSubscription_Item
  | Database_Procs_RankingsPlayerInternational_Item
  | Database_Procs_RankingsPlayerLocal_Item
  | Database_Procs_RankingsPlayerNation_Item
  | Database_Procs_RankingsPlayerState_Item
  | Database_Procs_RankingsSchoolInternational_Item
  | Database_Procs_RankingsSchoolLocal_Item
  | Database_Procs_RankingsSchoolNation_Item
  | Database_Procs_RankingsSchoolState_Item
  | Database_Procs_RankingsTeamInternational_Item
  | Database_Procs_RankingsTeamLocal_Item
  | Database_Procs_RankingsTeamNation_Item
  | Database_Procs_RankingsTeamState_Item;

type BaseRankingParams = {
  grade: SpecialGradeLevel;
  year: string;
};

type TeamRankingsParams = BaseRankingParams & {
  level: RankingLevel;
};

const rankColumn = defineNumericColumn<RankingResponseDataType, 'topRanking'>(
  'topRanking',
  { label: 'Rank', width: 's' },
);

// Player columns

export const playerColumns: readonly ModularColumnDef<
  | Database_Procs_RankingsPlayerLocal_Item
  | Database_Procs_RankingsPlayerNation_Item
>[] = [
  rankColumn,
  defineNumericColumn('stickers', { width: 'm' }),
  defineTextColumn('player'),
];

export const playerColumnsWithSchool: readonly ModularColumnDef<
  | Database_Procs_RankingsPlayerLocal_Item
  | Database_Procs_RankingsPlayerNation_Item
>[] = [...playerColumns, defineTextColumn('schoolName', { width: 'l' })];

export const playerColumnsWithDistrict: readonly ModularColumnDef<Database_Procs_RankingsPlayerState_Item>[] =
  [
    ...playerColumnsWithSchool,
    defineTextColumn('districtName', { width: 'l' }),
  ];

export const playerColumnsWithState: readonly ModularColumnDef<Database_Procs_RankingsPlayerNation_Item>[] =
  [...playerColumnsWithDistrict, defineTextColumn('state', { width: 's' })];

const individualPlayerColumns: readonly ModularColumnDef<Database_Procs_RankingsIndividualSubscription_Item>[] =
  [rankColumn, defineTextColumn('player'), defineNumericColumn('stickers')];

// Team columns

export const teamColumns: readonly ModularColumnDef<
  Database_Procs_RankingsTeamLocal_Item | Database_Procs_RankingsTeamNation_Item
>[] = [
  rankColumn,
  defineNumericColumn('stickersPerStudent', { width: 'm' }),
  defineNumericColumn('studentsPlaying', { width: 'm' }),
  defineTextColumn('teamName'),
  defineTextColumn('schoolName', { width: 'l' }),
];

export const teamColumnsWithDistrict: readonly ModularColumnDef<Database_Procs_RankingsTeamState_Item>[] =
  [...teamColumns, defineTextColumn('districtName')];

export const teamColumnsWithState: readonly ModularColumnDef<Database_Procs_RankingsTeamNation_Item>[] =
  [...teamColumnsWithDistrict, defineTextColumn('state', { width: 's' })];

// School columns

export const schoolColumns: readonly ModularColumnDef<
  | Database_Procs_RankingsSchoolLocal_Item
  | Database_Procs_RankingsSchoolState_Item
>[] = [
  rankColumn,
  defineNumericColumn('stickersPerStudent', { width: 'm' }),
  defineNumericColumn('studentsPlaying', { width: 'm' }),
  defineTextColumn('schoolName', { width: 'l' }),
];

export const schoolColumnsWithDistrict: readonly ModularColumnDef<Database_Procs_RankingsSchoolState_Item>[] =
  [...schoolColumns, defineTextColumn('districtName')];

export const schoolColumnsWithState: readonly ModularColumnDef<Database_Procs_RankingsSchoolNation_Item>[] =
  [...schoolColumnsWithDistrict, defineTextColumn('state', { width: 's' })];

export function defineTables(
  apiClient: Client,
): Record<
  RankingsSlug,
  ModularTableDef<RankingResponseDataType, TeamRankingsParams>
> {
  return {
    // School
    [RankingsSlug.SchoolInternational]: defineTable({
      title: 'Top schools in the world',
      columns: schoolColumnsWithState,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_SchoolInternational(params))
          .schoolInternationalRankings,
    }),
    [RankingsSlug.SchoolNation]: defineTable({
      title: 'Top schools in the nation',
      columns: schoolColumnsWithState,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_SchoolNation(params))
          .schoolNationalRankings,
    }),
    [RankingsSlug.SchoolState]: defineTable({
      title: 'Top schools in the state',
      columns: schoolColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_SchoolState(params)).schoolStatesRankings,
    }),
    [RankingsSlug.SchoolSystem]: defineTable({
      title: 'Top schools in the school system',
      columns: schoolColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_SchoolSystem(params))
          .schoolSystemRankings,
    }),
    [RankingsSlug.SchoolDistrict]: defineTable({
      title: 'Top schools in the school district',
      columns: schoolColumns,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_SchoolDistrict(params))
          .schoolDistrictRankings,
    }),

    // Teams
    [RankingsSlug.TeamInternational]: defineTable({
      title: 'Top teams in the world',
      columns: teamColumnsWithState,
      loader: async (params: TeamRankingsParams) =>
        (await apiClient.get_Rankings_TeamInternational(params))
          .teamInternationalRankings,
    }),
    [RankingsSlug.TeamNation]: defineTable({
      title: 'Top teams in the nation',
      columns: teamColumnsWithState,
      loader: async (params: TeamRankingsParams) =>
        (await apiClient.get_Rankings_TeamNation(params)).teamNationalRankings,
    }),
    [RankingsSlug.TeamState]: defineTable({
      title: 'Top teams in the state',
      columns: teamColumns,
      loader: async (params: TeamRankingsParams) =>
        (await apiClient.get_Rankings_TeamState(params)).teamStateRankings,
    }),
    [RankingsSlug.TeamSystem]: defineTable({
      title: 'Top teams in the school system',
      columns: teamColumns,
      loader: async (params: TeamRankingsParams) =>
        (await apiClient.get_Rankings_TeamSystem(params)).teamSystemRankings,
    }),
    [RankingsSlug.TeamDistrict]: defineTable({
      title: 'Top teams in the school district',
      columns: teamColumns,
      loader: async (params: TeamRankingsParams) =>
        (await apiClient.get_Rankings_TeamDistrict(params))
          .teamDistrictRankings,
    }),
    [RankingsSlug.TeamSchool]: defineTable({
      title: 'Top teams in the school',
      columns: teamColumns,
      loader: async (params: TeamRankingsParams) =>
        (await apiClient.get_Rankings_TeamSchool(params)).teamSchoolRankings,
    }),

    // Top Players
    [RankingsSlug.PlayerInternational]: defineTable({
      title: 'Top players in the world',
      columns: playerColumnsWithState,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_PlayerInternational(params))
          .playerInternationalRankings,
    }),
    [RankingsSlug.PlayerNation]: defineTable({
      title: 'Top players in the nation',
      columns: playerColumnsWithState,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_PlayerNation(params))
          .playerNationalRankings,
    }),
    [RankingsSlug.PlayerState]: defineTable({
      title: 'Top players in the state',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_PlayerState(params)).playerStateRankings,
    }),
    [RankingsSlug.PlayerSystem]: defineTable({
      title: 'Top players in the school system',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_PlayerSystem(params))
          .playerSystemRankings,
    }),
    [RankingsSlug.PlayerDistrict]: defineTable({
      title: 'Top players in the school district',
      columns: playerColumnsWithSchool,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_PlayerDistrict(params))
          .playerDistrictRankings,
    }),
    [RankingsSlug.PlayerSchool]: defineTable({
      title: 'Top players in the school',
      columns: playerColumns,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_PlayerSchool(params))
          .playerSchoolRankings,
    }),
    [RankingsSlug.PlayerIndividualNation]: defineTable({
      title: 'Top 100 Individual Subscription Players Nationally',
      columns: individualPlayerColumns,
      loader: async () =>
        (await apiClient.get_Rankings_Individual({ request: undefined }))
          .individualRankings,
    }),

    // Top Educators
    [RankingsSlug.EducatorNation]: defineTable({
      title: 'Top educator players in the nation',
      columns: playerColumnsWithState,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_EducatorNation(params))
          .educatorNationalRankings,
    }),
    [RankingsSlug.EducatorState]: defineTable({
      title: 'Top educator players in the state',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_EducatorState(params))
          .educatorStateRankings,
    }),
    [RankingsSlug.EducatorSystem]: defineTable({
      title: 'Top educator players in the school system',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_EducatorSystem(params))
          .educatorSystemRankings,
    }),
    [RankingsSlug.EducatorDistrict]: defineTable({
      title: 'Top educator players in the school district',
      columns: playerColumnsWithSchool,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_EducatorDistrict(params))
          .educatorDistrictRankings,
    }),
    [RankingsSlug.EducatorSchool]: defineTable({
      title: 'Top educator players in the school',
      columns: playerColumns,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_EducatorSchool(params))
          .educatorSchoolRankings,
    }),

    // Top Family Players
    [RankingsSlug.FamilyNation]: defineTable({
      title: 'Family Nation',
      columns: playerColumnsWithState,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_FamilyNation(params))
          .familyNationalRankings,
    }),
    [RankingsSlug.FamilyState]: defineTable({
      title: 'Family State',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_FamilyState(params)).familyStateRankings,
    }),
    [RankingsSlug.FamilySystem]: defineTable({
      title: 'Family School System',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_FamilySystem(params))
          .familySystemRankings,
    }),
    [RankingsSlug.FamilyDistrict]: defineTable({
      title: 'Family District',
      columns: playerColumnsWithSchool,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_FamilyDistrict(params))
          .familyDistrictRankings,
    }),
    [RankingsSlug.FamilySchool]: defineTable({
      title: 'Family School',
      columns: playerColumns,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_FamilySchool(params))
          .familySchoolRankings,
    }),

    // Top Family Teams
    [RankingsSlug.CombinedFamilyNation]: defineTable({
      title: 'Combined Family Nation',
      columns: playerColumnsWithState,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_CombinedFamilyNation(params))
          .combinedFamilyNationalRankings,
    }),
    [RankingsSlug.CombinedFamilyState]: defineTable({
      title: 'Combined Family State',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_CombinedFamilyState(params))
          .combinedFamilyStateRankings,
    }),
    [RankingsSlug.CombinedFamilySystem]: defineTable({
      title: 'Combined Family School System',
      columns: playerColumnsWithDistrict,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_CombinedFamilySystem(params))
          .combinedFamilySystemRankings,
    }),
    [RankingsSlug.CombinedFamilyDistrict]: defineTable({
      title: 'Combined Family District',
      columns: playerColumnsWithSchool,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_CombinedFamilyDistrict(params))
          .combinedFamilyDistrictRankings,
    }),
    [RankingsSlug.CombinedFamilySchool]: defineTable({
      title: 'Combined Family School',
      columns: playerColumns,
      loader: async (params: BaseRankingParams) =>
        (await apiClient.get_Rankings_CombinedFamilySchool(params))
          .combinedFamilySchoolRankings,
    }),
  } as const satisfies Record<
    RankingsSlug,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ModularTableDef<any, TeamRankingsParams>
  >;
}
